import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

interface WarpStreaksOverlayProps {
  duration?: number; // ms total
  onComplete?: () => void;
}

const WarpStreaksOverlay: React.FC<WarpStreaksOverlayProps> = ({ duration = 1500, onComplete }) => {
  const { viewport } = useThree();
  const meshRef = useRef<THREE.Mesh>(null);
  const startRef = useRef<number>(performance.now());
  const doneRef = useRef<boolean>(false);

  const material = useMemo(() => {
    const uniforms = {
      uTime: { value: 0 },
      uProgress: { value: 0 }, // 0..1..0 bell curve
      uResolution: { value: new THREE.Vector2(viewport.width, viewport.height) },
    };
    const vert = `
      varying vec2 vUv;
      void main(){
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
    const frag = `
      precision highp float;
      varying vec2 vUv;
      uniform float uTime;
      uniform float uProgress;
      
      float hash(float n){ return fract(sin(n)*43758.5453123); }
      float smoothPulse(float x, float a, float b){
        float t = clamp((x - a) / max(1e-5, (b - a)), 0.0, 1.0);
        return smoothstep(0.0, 1.0, t) * smoothstep(1.0, 0.0, t);
      }

      void main(){
        // Convert uv to centered coordinates
        vec2 uv = vUv;
        float t = uTime * 1.4;

        // Bell curve progress 0->1->0 for intensity
        float p = uProgress; // already bell-shaped from parent

        // Base background dim
        float col = 0.0;

        // Multi-layer streaks (12 lines)
        for(int i=0;i<12;i++){
          float fi = float(i);
          float seed = hash(12.3*fi + 0.17);
          float cx = fract(seed*7.13 + fi*0.137);
          float width = mix(0.002, 0.008, hash(fi*9.1));
          float speed = mix(0.6, 1.6, hash(fi*3.7));
          // subtle horizontal drift
          float drift = (sin(t*0.7 + fi)*0.02);
          float d = abs((uv.x + drift) - cx);
          float line = smoothstep(width, 0.0, width - d);
          // Vertical envelope moving to simulate flow
          float head = fract(uv.y + t*speed + hash(fi*5.3));
          float seg = smoothPulse(head, 0.05, 0.35);
          col += line * seg;
        }

        // Soften and scale by progress
        col = pow(col, 0.7) * (0.85 * p);
        // Subtle vignette to keep edges calm
        vec2 c = uv - 0.5;
        float v = smoothstep(0.9, 0.1, length(c));
        col *= v;

        vec3 color = vec3(0.7, 0.82, 1.0) * col;
        gl_FragColor = vec4(color, col);
      }
    `;
    const m = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: vert,
      fragmentShader: frag,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    return m;
  }, [viewport.width, viewport.height]);

  useEffect(() => {
    startRef.current = performance.now();
    const id = window.setTimeout(() => {
      if (doneRef.current) return;
      doneRef.current = true;
      onComplete && onComplete();
    }, duration);
    return () => window.clearTimeout(id);
  }, [duration, onComplete]);

  useFrame((_, delta) => {
    const m = meshRef.current?.material as THREE.ShaderMaterial | undefined;
    if (!m) return;
    const elapsed = performance.now() - startRef.current;
    const t = Math.max(0, Math.min(1, elapsed / duration));
    // bell-shaped progress for intensity
  const bell = Math.sin(Math.PI * t);
    m.uniforms.uTime.value += delta;
    m.uniforms.uProgress.value = bell;
    m.needsUpdate = true;
  });

  return (
    <mesh ref={meshRef} position={[0,0,0]} scale={[viewport.width, viewport.height, 1]}>
      <planeGeometry args={[1,1]} />
      {/* @ts-ignore */}
      <primitive object={material} attach="material" />
    </mesh>
  );
};

export default WarpStreaksOverlay;
